import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import VerificationSuccess from "@/components/VerificationSuccess";
import { apiRequest } from "@/lib/queryClient";

export default function VerifyEmail() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Get the token and userId from the URL
        const queryParams = new URLSearchParams(window.location.search);
        const token = queryParams.get("token");
        const userId = queryParams.get("userId");

        if (!token || !userId) {
          setError("Invalid verification link. Missing token or user ID.");
          setVerifying(false);
          return;
        }

        // Call the verification API
        const response = await apiRequest(
          "GET",
          `/api/verify-email?userId=${userId}&token=${token}`
        );
        
        const data = await response.json();
        
        if (data.success) {
          setVerified(true);
        } else {
          setError(data.message || "Verification failed. Please try again.");
        }
      } catch (err) {
        setError("An error occurred during verification. Please try again later.");
        console.error("Verification error:", err);
      } finally {
        setVerifying(false);
      }
    };

    verifyEmail();
  }, [toast]);

  // While verifying, show a loading state
  if (verifying) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-full max-w-md mx-auto">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Verifying Email</CardTitle>
            <CardDescription className="text-center">
              Please wait while we verify your email
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center p-6">
            <Loader2 className="h-12 w-12 animate-spin" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // If verified successfully
  if (verified) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <VerificationSuccess />
      </div>
    );
  }

  // If verification failed
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Verification Failed</CardTitle>
          <CardDescription className="text-center">
            We could not verify your email
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center p-6">
            <p className="text-destructive mb-4">{error}</p>
            <p className="mb-4">
              This may be because the verification link has expired or has already been used.
            </p>
            <button
              className="text-primary hover:underline"
              onClick={() => setLocation("/auth")}
            >
              Return to login
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}